import dayjs from 'dayjs';
import { isNumber } from 'lodash';
import orderBy from 'lodash/orderBy';
import * as React from 'react';
import { AutoSizer } from 'react-virtualized';
import {
  CartesianGrid,
  ErrorBar,
  Legend,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  IAggregateWithMetric,
  IComputedAggregate,
  ISize,
} from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { EPdfMode } from '../pdf/Pdf';
import { legendFormatter } from './legendFormatter';
import { MetricTooltip } from './MetricTooltip';

interface IProps {
  selectedMetricID: string;
  sessions: IComputedAggregate[];
  pdfMode?: EPdfMode;
  whiskers: boolean;
}

export const TrendGraph = (props: IProps): React.ReactElement => {
  const { selectedMetricID, sessions } = props;

  const data = React.useMemo(() => {
    return orderBy(
      sessions,
      (agg: IComputedAggregate): number => +agg.meta.id
    ).reduce((acc, aggregate) => {
      const metric: IAggregateWithMetric = aggregate.data[
        selectedMetricID
      ] as IAggregateWithMetric;
      if (!metric) return acc;
      else {
        const val = roundValue(metric.mean, metric.roundingPrecision);
        acc.unshift({
          name: metric.label,
          Date: Number(aggregate.meta.id),
          y: val,
          metric,
          sessionId: aggregate.meta.id,
          extremes: [
            // Session min
            isNumber(metric.bottomValues[0])
              ? val - metric.bottomValues[0]
              : null,
            // Session max
            isNumber(metric.topValues[0]) ? metric.topValues[0] - val : null,
          ],
          extremesValues: {
            min: roundValue(metric.bottomValues[0], metric.roundingPrecision),
            max: roundValue(metric.topValues[0], metric.roundingPrecision),
          },
          whiskers: props.whiskers,
        });
      }

      return acc;
    }, []);
  }, [sessions?.length, selectedMetricID]);

  return (
    <React.Fragment>
      <div
        style={{
          height: '80mm',
          width: '100%',
        }}
        className='graph-parent'
      >
        <AutoSizer>
          {(size: ISize): React.ReactElement => {
            const tickFormatter = (tick: number): string =>
              tick > 12875256000
                ? dayjs.utc(Number(tick)).format('M/D')
                : tick.toString();

            const shape = (e: {
              cx: number;
              cy: number;
              fill: string;
              height: number;
              width: number;
              y: number;
              x: number;
            }): React.ReactElement => {
              return (
                <circle
                  style={{ cursor: 'pointer' }}
                  cx={e.cx}
                  cy={e.cy}
                  fill={e.fill}
                  height={e.height}
                  width={e.width}
                  y={e.y}
                  x={e.x}
                  r={4}
                />
              );
            };

            return (
              <ScatterChart
                width={size.width}
                height={size.height}
                margin={{ top: 2, right: 20, bottom: 2, left: 0 }}
              >
                <CartesianGrid stroke='#ccc' strokeWidth={0.25} />
                <XAxis
                  type='number'
                  scale='time'
                  dataKey='Date'
                  domain={['auto', 'auto']}
                  tickFormatter={tickFormatter}
                  interval={'preserveStartEnd'}
                  tick={{
                    fill:
                      props.pdfMode === EPdfMode.PRINT
                        ? 'var(--color-text-dark)'
                        : 'var(--color-text)',
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type='number'
                  dataKey='y'
                  domain={['auto', 'auto']}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill:
                      props.pdfMode === EPdfMode.PRINT
                        ? 'var(--color-text-dark)'
                        : 'var(--color-text)',
                  }}
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={tickFormatter}
                  content={MetricTooltip}
                />
                <Scatter
                  name='Session Averages'
                  data={data}
                  fill={
                    props.pdfMode === EPdfMode.PRINT
                      ? 'var(--color-scatter-primary-inverse)'
                      : 'var(--color-scatter-primary)'
                  }
                  line={{
                    stroke:
                      props.pdfMode === EPdfMode.PRINT
                        ? 'var(--color-dark-text)'
                        : 'var(--color-text)',
                    strokeWidth: 2,
                  }}
                  isAnimationActive={false}
                  lineType='fitting'
                  shape={shape}
                >
                  {props.whiskers && (
                    <ErrorBar
                      dataKey='extremes'
                      stroke='#ffffff50'
                      strokeWidth={2}
                    />
                  )}
                </Scatter>
                <Legend
                  formatter={(value) => legendFormatter(value, props.pdfMode)}
                />
              </ScatterChart>
            );
          }}
        </AutoSizer>
      </div>
    </React.Fragment>
  );
};
